import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { dataService } from "../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, ToggleButtonGroup, ToggleButton, InputAdornment } from "@mui/material";
import { getAuth } from "firebase/auth";
import { X, ChevronDown, EllipsisVertical, ChevronLeft } from "lucide-react";
import CustomCheckbox from "../../Shared/Checkbox";
import Button from "../../Shared/Buttons/Button";
import { useTranslation } from 'react-i18next';

export default function CompanyCrudDialog({ dialogOpen, closeDialog, ...props }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const auth = getAuth();

  const [companyToEdit, setCompanyToEdit] = useState(null);

  const [allCurrencies, setAllCurrencies] = useState([]);

  const [generalError, setGeneralError] = useState(null);

  const [companyName, setCompanyName] = useState(null);
  const [companyNameError, setCompanyNameError] = useState(null);

  const [currency, setCurrency] = useState(null);
  const [currencyError, setCurrencyError] = useState(null);

  const [companyHidden, setCompanyHidden] = useState(false);

  // Dropdown
  const [openDropdown, setOpenDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // Position of the dropdown, relative to the dropdown button
  const openDropdownButtonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Three-dot-menu
  const [openThreeDotMenu, setOpenThreeDotMenu] = useState(false);
  const [threeDotPosition, setThreeDotPosition] = useState({ top: 0, left: 0 }); // Position of the dropdown, relative to the dropdown button
  const openThreeDotMenuButtonRef = useRef(null);
  const threeDotMenuRef = useRef(null);

  // Transfer company
  const [transferCompany, setTransferCompany] = useState(false);
  const [subscribers, setSubscribers] = useState([]);

  // Clone company
  const [cloneMode, setCloneMode] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [cloneLoading, setCloneLoading] = useState(false);

  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [subscriberError, setSubscriberError] = useState(null);

  const [transferUsers, setTransferUsers] = useState(false);

  // fetch all currencies
  useEffect(() => {
    dataService
      .getAllCurrencies()
      .then((response) => {
        if (response) {
          setAllCurrencies(response);
          if (!props.companyToEdit) {
            setCurrency(response[1]); // set default currency to euro
          } else {
            // find the currency of the company to edit
            var currency = response.find((currency) => currency.id === props.companyToEdit.currencyId);
            setCurrency(currency);
          }
        } else {
          setGeneralError(t('companyCrudDialog.fetchCurrenciesFailed'));
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (props.companyToEdit) {
      setCompanyToEdit(props.companyToEdit);
      setCompanyName(props.companyToEdit.name);
      // setCurrency({ id: props.companyToEdit.currencyId });
    }
  }, [props.companyToEdit]);

  useEffect(() => {
    if (openDropdown && openDropdownButtonRef.current) {
      // Calculate the position of the dropdown relative to the viewport
      const rect = openDropdownButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // Position dropdown below the trigger
        left: rect.left + window.scrollX, // Align to the left of the trigger
      });
    }
  }, [openDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && openDropdown) {
        // check if target is not the button
        if (!openDropdownButtonRef.current.contains(event.target)) {
          setOpenDropdown(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    if (openThreeDotMenu && openThreeDotMenuButtonRef.current) {
      // Calculate the position of the dropdown relative to the viewport
      const rect = openThreeDotMenuButtonRef.current.getBoundingClientRect();
      setThreeDotPosition({
        top: rect.bottom + window.scrollY + 4, // Position dropdown below the trigger
        left: rect.left + window.scrollX, // Align to the right of the trigger
      });
    }
  }, [openThreeDotMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (threeDotMenuRef.current && !threeDotMenuRef.current.contains(event.target) && openThreeDotMenu) {
        // check if target is not the button
        if (!openThreeDotMenuButtonRef.current.contains(event.target)) {
          setOpenThreeDotMenu(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openThreeDotMenu]);

  const changeCompanyName = (e) => {
    setCompanyName(e.target.value);
    if (e.target.value.length > 0) {
      setCompanyNameError(null);
    }
  };

  const handleCurrencySelection = (currency) => {
    setCurrency(currency);
    setOpenDropdown(false);
  };

  const checkFields = () => {
    var disabled = false;
    if (companyName === "" || companyName === null || companyName === undefined) {
      disabled = true;
    }
    return disabled;
  };

  const validateFields = () => {
    var validationGood = true;
    if (companyName === "" || companyName === null || companyName === undefined) {
      validationGood = false;
      setCompanyNameError(t('companyCrudDialog.companyNameRequired'));
    }

    if (currency === null || currency === undefined) {
      validationGood = false;
      setCurrencyError(t('companyCrudDialog.currencyRequired'));
    }

    return validationGood;
  };

  const handleHideCompany = () => {
    setOpenThreeDotMenu(false);
    setGeneralError(null);
    const newHiddenState = !props.companyToEdit.isHidden;
    dataService
      .hideCompanyFromUserIdByCompanyId(props.companyToEdit.id, props.Login.userid, newHiddenState)
      .then((response) => {
        if (response.success) {
          if (props.onCompanyHidden) {
            props.onCompanyHidden(props.companyToEdit.id, newHiddenState);
          }
          closeDialog();
        } else {
          setGeneralError(t('companyCrudDialog.hideCompanyFailed'));
        }
      })
      .catch((error) => {
        if (error.status === 403) {
          setGeneralError(t('companyCrudDialog.noAccess'));
        } else {
          setGeneralError(t('companyCrudDialog.hideCompanyFailed'));
        }
      });
  };

  const handleSetTransferCompany = (value) => {
    if (value === true && subscribers.length === 0) {
      getSubcribers();
    }
    setOpenThreeDotMenu(false);
    setTransferCompany(value);
  };

  const handleSetCloneMode = () => {
    setOpenThreeDotMenu(false);
    setCloneName(t('companyCrudDialog.copyOf') + (props.companyToEdit ? props.companyToEdit.name : ""));
    setCloneMode(true);
  };

  const handleSubscriberSelection = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setOpenDropdown(false);
    setSubscriberError(null);
  };

  const getSubcribers = () => {
    dataService
      .getAllSubscribers()
      .then((response) => {
        if (response) {
          setSubscribers(response);
        } else {
          setGeneralError(t('companyCrudDialog.fetchSubscribersFailed'));
        }
      })
      .catch((error) => {
        if (error.status === 403) {
          setGeneralError(t('companyCrudDialog.noAccess'));
        }
      });
  };

  const handleSaveCompany = () => {
    if (cloneMode) {
      if (!cloneName || cloneName.trim() === "") {
        setGeneralError(t('companyCrudDialog.companyNameRequired'));
        return;
      }
      setCloneLoading(true);
      dataService
        .cloneCompany(props.companyToEdit.id, cloneName.trim(), props.userId)
        .then((response) => {
          if (response.success) {
            props.onCompanyCloned();
          } else {
            setCloneLoading(false);
            setGeneralError(response.message);
          }
        })
        .catch((error) => {
          console.log(error);
          setCloneLoading(false);
          setGeneralError(t('companyCrudDialog.cloneCompanyFailed'));
        });
      return;
    }
    if (transferCompany) {
      if (selectedSubscriber === null) {
        setSubscriberError(t('companyCrudDialog.subscriberRequired'));
        return;
      }
      if (companyToEdit.subscriberUniqueId === selectedSubscriber.uniqueId) {
        setGeneralError(t('companyCrudDialog.sameSubscriberError'));
        return;
      }
      let transferCompanyObject = {
        companyId: companyToEdit.id,
        receivingSubscriberUniqueId: selectedSubscriber.uniqueId,
        transferUsers: transferUsers,
      };
      dataService
        .transferCompany(transferCompanyObject)
        .then((response) => {
          if (response.success) {
            window.location.reload();
          } else {
            setGeneralError(response.message);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else if (validateFields()) {
      if (props.companyToEdit) {
        var ModifyCompanyObject = {
          id: props.companyToEdit.id,
          name: companyName,
          currencyId: currency.id,
          updatedByUserId: props.userId,
          subscriberUniqueId: props.subscriberUniqueId,
        };

        dataService
          .modifyCompany(ModifyCompanyObject)
          .then((response) => {
            if (response.success) {
              window.location.reload();
            } else {
              setGeneralError(response.message);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        var CreateCompanyObject = {
          name: companyName,
          currencyId: currency.id,
          insertedByUserId: props.userId,
          subscriberUniqueId: props.subscriberUniqueId,
        };

        dataService
          .createCompany(CreateCompanyObject)
          .then((response) => {
            if (response.success) {
              window.location.reload();
            } else {
              setGeneralError(response.message);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  };

  const handleOpenThreeDotMenu = () => {
    setOpenThreeDotMenu(true);
  };

  const handleSetCompanyState = (companyId) => {
    dataService
      .checkSubscriptionCompanyLimitByCompanyId(companyId)
      .then((result) => {
        let companyLimitReached = result;

        if (companyLimitReached && !companyToEdit.isActive) {
          setOpenThreeDotMenu(false);
          setGeneralError(t('companyCrudDialog.companyLimitReached'));
        } else {
          dataService
            .setCompanyState(companyId, !companyToEdit.isActive)
            .then((response) => {
              window.location.reload();
            })
            .catch((error) => {
              setOpenThreeDotMenu(false);
              if (error.status === 403) {
                setGeneralError(t('companyCrudDialog.enterpriseRequired'));
              } else {
                setGeneralError(companyToEdit.isActive ? t('companyCrudDialog.disableCompanyFailed') : t('companyCrudDialog.enableCompanyFailed'));
              }
            });
        }
      })
      .catch((error) => {
        setGeneralError(companyToEdit.isActive ? t('companyCrudDialog.disableCompanyFailed') : t('companyCrudDialog.enableCompanyFailed'));
      });
  };

  return (
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" onClose={closeDialog} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
          {props.companyToEdit ? (cloneMode ? t('companyCrudDialog.cloneCompany') : transferCompany ? t('companyCrudDialog.transferCompany') : t('companyCrudDialog.editCompany')) : t('companyCrudDialog.createCompany')}
        </DialogTitle>
        <div className={classes.head__buttonContainer}>
          {companyToEdit && (
            <>
              <button ref={openThreeDotMenuButtonRef} onClick={() => handleOpenThreeDotMenu()} className={classes.dialog__threeDotButton}>
                <EllipsisVertical size={19} />
              </button>
              {ReactDOM.createPortal(
                <div
                  ref={threeDotMenuRef}
                  className={classes.threeDotMenuDropdownContainer}
                  style={{ display: openThreeDotMenu ? "block" : "none", top: threeDotPosition.top, left: threeDotPosition.left }}
                >
                  {/* check if companyToEdit.users has a user with id === props.Login.userid */}
                  {companyToEdit.users && companyToEdit.users.length > 0 && companyToEdit.users.some((user) => user.id === props.Login.userid) && (
                    <button className={classes.dropdown__menuItem} onClick={() => handleHideCompany()}>
                      {companyToEdit.isHidden ? t('companyCrudDialog.unhideCompany') : t('companyCrudDialog.hideCompany')}
                    </button>
                  )}
                  {props.Login.isTaplrAdmin && (
                    <button className={classes.dropdown__menuItem} onClick={() => handleSetTransferCompany(true)}>
                      {t('companyCrudDialog.transferCompany')}
                    </button>
                  )}
                  <button className={classes.dropdown__menuItem} onClick={() => handleSetCloneMode()}>
                    {t('companyCrudDialog.cloneCompany')}
                  </button>
                  <button className={companyToEdit.isActive ? classes.dropdown__menuItem_negative : classes.dropdown__menuItem_positive} onClick={() => handleSetCompanyState(props.companyToEdit.id)}>
                    {companyToEdit.isActive ? t('companyCrudDialog.disable') : t('companyCrudDialog.enable')}
                  </button>
                </div>,
                document.body
              )}
            </>
          )}
          <button onClick={() => closeDialog()} className={classes.dialog__closeButton}>
            <X size={19} />
          </button>
        </div>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          {!props.companyToEdit && <p className={classes.dialog__subtitle}>{t('companyCrudDialog.createSubtitle')}</p>}
          {generalError && (
            <div className={classes.errorContainer}>
              <p className={classes.errorMessage}>{generalError}</p>
            </div>
          )}
          {cloneMode ? (
            <div className={classes.form}>
              <div className={classes.inputContainer}>
                <label className={classes.labelText}>{t('companyCrudDialog.newCompanyName')}</label>
                <TextField
                  className={classes.textField}
                  type={"text"}
                  variant="outlined"
                  size="small"
                  onChange={(e) => setCloneName(e.target.value)}
                  value={cloneName}
                  fullWidth={true}
                  autoFocus
                ></TextField>
              </div>
            </div>
          ) : transferCompany ? (
            <div className={classes.form}>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>{t('companyCrudDialog.subscriber')}</label>
                  <p className={classes.inputError}>{subscriberError}</p>
                </div>
                <div className={classes.container}>
                  <button
                    ref={openDropdownButtonRef}
                    className={classes.dropdown__inputContainer}
                    style={{ borderColor: subscriberError ? "#D84949" : "#B8B8B8" }}
                    onClick={() => setOpenDropdown(!openDropdown)}
                  >
                    <input type="text" className={classes.input} placeholder={t('companyCrudDialog.selectSubscriber')} value={selectedSubscriber && selectedSubscriber.name} readOnly />
                    <ChevronDown className={classes.input__icon} style={{ transform: `rotate(${openDropdown ? "180deg" : "0deg"})` }} />
                  </button>
                  {ReactDOM.createPortal(
                    <div ref={dropdownRef} className={classes.dropdownContainer} style={{ display: openDropdown ? "block" : "none", top: dropdownPosition.top, left: dropdownPosition.left }}>
                      {subscribers.map((subscriber) => (
                        <button key={subscriber.id} className={classes.dropdown__menuItem} onClick={() => handleSubscriberSelection(subscriber)}>
                          {subscriber.name}
                        </button>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
              </div>
              {/* <div className={classes.inputContainer}>
                                <CustomCheckbox checked={transferUsers} onChange={setTransferUsers} label="Transfer users" />
                            </div> */}
            </div>
          ) : (
            <div className={classes.form}>
              <div className={classes.inputContainer}>
                <label className={classes.labelText}>{t('companyCrudDialog.companyName')}</label>
                <TextField
                  className={classes.textField}
                  type={"text"}
                  variant="outlined"
                  size="small"
                  onChange={(event) => changeCompanyName(event)}
                  value={companyName}
                  fullWidth={true}
                  error={companyNameError !== null}
                  helperText={companyNameError}
                ></TextField>
              </div>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>{t('companyCrudDialog.currency')}</label>
                  <p className={classes.inputError}>{currencyError}</p>
                </div>
                <div className={classes.container}>
                  <button
                    ref={openDropdownButtonRef}
                    className={classes.dropdown__inputContainer}
                    style={{ borderColor: currencyError ? "#D84949" : "#B8B8B8" }}
                    onClick={() => setOpenDropdown(!openDropdown)}
                  >
                    <input type="text" className={classes.input} value={currency ? currency.name + " " + currency.symbol : ""} readOnly />
                    <ChevronDown className={classes.input__icon} style={{ transform: `rotate(${openDropdown ? "180deg" : "0deg"})` }} />
                  </button>
                  {ReactDOM.createPortal(
                    <div ref={dropdownRef} className={classes.dropdownContainer} style={{ display: openDropdown ? "block" : "none", top: dropdownPosition.top, left: dropdownPosition.left }}>
                      {allCurrencies.map((currency) => (
                        <button key={currency.id} className={classes.dropdown__menuItem} onClick={() => handleCurrencySelection(currency)}>
                          {currency.name} {currency.symbol}
                        </button>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        {cloneMode || transferCompany ? (
          <Button variant="ghost" startIcon={<ChevronLeft />} onClick={() => { setCloneMode(false); setTransferCompany(false); }}>
            {t('common.back')}
          </Button>
        ) : (
          <Button variant="outlined" onClick={closeDialog}>
            {t('common.cancel')}
          </Button>
        )}

        <Button disabled={cloneMode ? (!cloneName || cloneName.trim() === "") : checkFields()} loading={cloneLoading} onClick={() => handleSaveCompany()}>
          {cloneMode ? t('companyCrudDialog.cloneCompany') : props.companyToEdit ? (transferCompany ? t('companyCrudDialog.transferCompany') : t('companyCrudDialog.saveChanges')) : t('companyCrudDialog.createCompany')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "400px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    lineHeight: "100% !important",

    padding: "0px !important",
  },

  head__buttonContainer: {
    display: "flex",
    gap: "8px",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__threeDotButton: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",
    opacity: "0.8",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  toggleFilter: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    position: "relative",

    display: "flex",
    alignItems: "center",

    backgroundColor: "#DCDCDC",
    borderRadius: "4px",

    padding: "1px",
    marginBottom: "16px",
  },

  toggleFilter__optionButton: {
    height: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",

    border: "none",
    borderRadius: "3px",

    color: "#0C0E29",
    backgroundColor: "transparent",

    transition: "all ease-in-out 0.2s",

    zIndex: "1",

    "&:hover": {
      // backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
  },

  toggleFilter__label: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0",
  },

  toggleSelected: {
    width: "calc(50% - 1px)",
    height: "30px",
    position: "absolute",
    transition: "transform 0.2s",

    backgroundColor: "white",
    borderRadius: "3px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "8px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  form: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  inputContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  // Dropdown
  container: {
    position: "relative",
  },

  dropdown__inputContainer: {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",

    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    border: "1px solid",
    borderRadius: "4px",
    backgroundColor: "transparent",

    padding: "0px",

    "&:hover": {
      border: "1px solid #0C0E29",
      cursor: "pointer",
    },
  },

  input: {
    width: "100%",
    height: "40px",
    padding: "0 14px",
    paddingRight: "32px",
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    color: "#0C0E29",
    fontSize: "16px",
    textAlign: "left",

    fontFamily: "Lato, sans-serif",

    "&:hover": {
      cursor: "pointer",
    },
  },

  input__icon: {
    position: "absolute",
    right: "10px",
    width: "20px",
    height: "20px",
    color: "#0C0E29",
  },

  threeDotMenuDropdownContainer: {
    width: "fit-content",
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    transform: "translate(calc(-100% + 25px), 0)",
    // top: 'calc(100% + 4px)',
    // left: '0',
    zIndex: "1400",
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",

    display: "flex",
    flexDirection: "column",

    padding: "4px",

    "&::-webkit-scrollbar": {
      width: "4px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  dropdownContainer: {
    width: "fit-content",
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    // top: 'calc(100% + 4px)',
    // left: '0',
    zIndex: "1400",
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",

    display: "flex",
    flexDirection: "column",

    padding: "4px",

    "&::-webkit-scrollbar": {
      width: "4px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  dropdown__collapseButton: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "rgba(12, 14, 41, 0.6)",
    backgroundColor: "#FFFFFF",

    // no text wrapping
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    "&:hover": {
      backgroundColor: "#F4F4F4",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  collapseButton__icon: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
  },

  dropdown__menuItem: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F4F4F4",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__menuItem_negative: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#D84949",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#FDF3F3",
      color: "#C73B3B",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__menuItem_positive: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#3AB037",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F3FBF2",
      color: "#2B9128",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

}));
